import { useApiClient } from '@/composables/useApiClient'
import { useToast } from 'primevue'
import { useI18n } from 'vue-i18n'
import type {
  IOperatorSessionResponse,
  IPauseQueues,
  IPauseQueuesResponse,
  IRegisterOperatorSession,
  IRegisterQueues,
  IRegisterWhatsapp,
  IUnregisterOperatorSession,
  IUnregisterWhatsapp,
} from './interfaces/IRegisterQueues'

interface RequestOptions {
  notify?: boolean
}

interface IManualCallIntent {
  extension: string
  campaign_id: string
  phone: string
  contact_id?: string | null
}


export const useCallService = () => {
  const privateApi = useApiClient(true)
  const toast = useToast()
  const { t } = useI18n()

  const showToast = (type: 'success' | 'error', messageKey: string) => {
    toast.add({
      severity: type,
      summary: t(type === 'success' ? 'general.success' : 'general.error'),
      detail: t(messageKey),
      life: 3000,
    })
  }

  const shouldNotify = (options?: RequestOptions) => options?.notify !== false

  const handleError = (_error: unknown, messageKey: string, options?: RequestOptions) => {
    if (shouldNotify(options)) {
      showToast('error', messageKey)
    }
  }

  const registerQueues = async (
    registerQueuesData: IRegisterQueues,
    options?: RequestOptions,
  ): Promise<boolean> => {
    try {
      const result = await privateApi.post<boolean, IRegisterQueues>(
        '/oper/call/register-queue',
        registerQueuesData,
      )
      if (shouldNotify(options)) {
        showToast('success', 'call.success_register_queues')
      }
      return result
    } catch (error) {
      handleError(error, 'call.error_register_queues', options)
      throw error
    }
  }

  const registerWhatsapp = async (
    registerWhatsappData: IRegisterWhatsapp,
    options?: RequestOptions,
  ): Promise<boolean> => {
    try {
      const result = await privateApi.post<boolean, IRegisterWhatsapp>(
        '/oper/whatsapp/campaign-login/register',
        registerWhatsappData,
      )
      if (shouldNotify(options)) {
        showToast('success', 'call.success_register_queues')
      }
      return result
    } catch (error) {
      handleError(error, 'call.error_register_queues', options)
      throw error
    }
  }

  const unregisterWhatsapp = async (
    unregisterWhatsappData: IUnregisterWhatsapp,
    options?: RequestOptions,
  ): Promise<boolean> => {
    try {
      const result = await privateApi.post<boolean, IUnregisterWhatsapp>(
        '/oper/whatsapp/campaign-login/unregister',
        unregisterWhatsappData,
      )
      if (shouldNotify(options)) {
        showToast('success', 'call.success_unregister_queues')
      }
      return result
    } catch (error) {
      handleError(error, 'call.error_unregister_queues', options)
      throw error
    }
  }

  const unregisterQueues = async (
    registerQueuesData: IRegisterQueues,
    options?: RequestOptions,
  ): Promise<boolean> => {
    try {
      const result = await privateApi.post<boolean, IRegisterQueues>(
        '/oper/call/unregister-queue',
        registerQueuesData,
      )

      if (shouldNotify(options)) {
        showToast('success', 'call.success_unregister_queues')
      }
      return result
    } catch (error) {
      handleError(error, 'call.error_unregister_queues', options)
      throw error
    }
  }

  const registerOperatorSession = async (
    registerOperatorSessionData: IRegisterOperatorSession,
    options?: RequestOptions,
  ): Promise<IOperatorSessionResponse> => {
    try {
      const result = await privateApi.post<IOperatorSessionResponse, IRegisterOperatorSession>(
        '/oper/operator-session/register',
        registerOperatorSessionData,
      )
      if (shouldNotify(options)) {
        showToast('success', 'call.success_register_queues')
      }
      return result
    } catch (error) {
      handleError(error, 'call.error_register_queues', options)
      throw error
    }
  }

  const unregisterOperatorSession = async (
    unregisterOperatorSessionData: IUnregisterOperatorSession,
    options?: RequestOptions,
  ): Promise<IOperatorSessionResponse> => {
    try {
      const result = await privateApi.post<IOperatorSessionResponse, IUnregisterOperatorSession>(
        '/oper/operator-session/unregister',
        unregisterOperatorSessionData,
      )
      if (shouldNotify(options)) {
        showToast('success', 'call.success_unregister_queues')
      }
      return result
    } catch (error) {
      handleError(error, 'call.error_unregister_queues', options)
      throw error
    }
  }

  const pauseQueues = async (
    pauseQueuesData: IPauseQueues,
    options?: RequestOptions,
  ): Promise<IPauseQueuesResponse> => {
    try {
      const result = await privateApi.post<IPauseQueuesResponse, IPauseQueues>(
        '/oper/call/pause-queue',
        pauseQueuesData,
      )

      if (result.status && shouldNotify(options)) {
        showToast(
          'success',
          pauseQueuesData.is_paused ? 'call.success_pause_queues' : 'call.success_unpause_queues',
        )
      } else if (!result.status && shouldNotify(options)) {
        showToast('error', 'call.error_pause_queues')
      }
      return result
    } catch (error) {
      handleError(error, 'call.error_pause_queues', options)
      throw error
    }
  }
  const registerManualCallIntent = async (
    data: IManualCallIntent,
    options?: RequestOptions,
  ): Promise<boolean> => {
    try {
      return await privateApi.post<boolean, IManualCallIntent>(
        '/oper/call/manual-call-intent',
        data,
      )
    } catch (error) {
      handleError(error, 'call.error_manual_call_intent', options)
      throw error
    }
  }


  return {
    registerQueues,
    registerWhatsapp,
    registerOperatorSession,
    unregisterQueues,
    unregisterWhatsapp,
    unregisterOperatorSession,
    pauseQueues,
    registerManualCallIntent,
  }
}
